import React, { useState } from 'react';
import { InspectionDetail } from './InspectionDetail';
import { ChatInterface } from '../Chat/ChatInterface';
import { Inspection } from '../../types/inspection';

interface InspectionChatWrapperProps {
    inspection: Inspection;
    garageId: string;
    garageName: string;
    onClose: () => void;
    onCreateQuote: () => void;
    onStatusChange: (id: string, status: string) => void;
    credits?: { standard: number, ai: number } | null;
    initialShowChat?: boolean;
    onMessagesRead?: () => void;
    onInspectionUpdated?: (inspectionId: string) => Promise<void>;
}

export const InspectionChatWrapper: React.FC<InspectionChatWrapperProps> = ({
                                                                                inspection, garageId, garageName, onClose, onCreateQuote, onStatusChange,
                                                                                credits, initialShowChat = false, onMessagesRead, onInspectionUpdated
                                                                            }) => {
    const [showChat, setShowChat] = useState(initialShowChat);
    const [currentInspection, setCurrentInspection] = useState<Inspection>(inspection);

    const isChatDisabled = () => {
        if (currentInspection.status === 'completed' || currentInspection.status === 'rejected') return true;
        if (currentInspection.quotes && currentInspection.quotes.length > 0) {
            const hasAcceptedQuoteFromOtherGarage = currentInspection.quotes.some(
                quote => (quote.status === 'accepted_with_payment' || quote.status === 'accepted_no_payment') && quote.garageId !== garageId
            );
            if (hasAcceptedQuoteFromOtherGarage) return true;
        }
        if (currentInspection.garageResponses && currentInspection.garageResponses.length > 0) {
            const hasRejectedResponse = currentInspection.garageResponses.some(
                response => response.garageId === garageId && response.status === 'rejected'
            );
            if (hasRejectedResponse) return true;
        }
        return false;
    };

    const handleStartChat = () => { setShowChat(true); };

    const handleBackToInspection = () => {
        setShowChat(false);
        if (onInspectionUpdated) onInspectionUpdated(currentInspection._id);
    };

    const handleInspectionUpdated = async () => {
        if (onInspectionUpdated) await onInspectionUpdated(currentInspection._id);
    };

    const handleStatusChange = async (id: string, status: string) => {
        await onStatusChange(id, status);
        setCurrentInspection({ ...currentInspection, status: status as any });
        if (onInspectionUpdated) await onInspectionUpdated(id);
    };

    if (showChat) {
        return (
            // Full-screen on mobile, centered modal on larger screens
            <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center">
                <div className="bg-white w-full h-[90vh] sm:rounded-lg sm:w-full sm:max-w-2xl sm:h-[80vh]">
                    <ChatInterface
                        inspectionId={currentInspection._id}
                        garageId={garageId}
                        garageName={garageName}
                        onClose={initialShowChat ? onClose : handleBackToInspection}
                        onMessagesRead={onMessagesRead}
                        inspectionStatus={currentInspection.status}
                        isDisabled={isChatDisabled()}
                    />
                </div>
            </div>
        );
    }

    return (
        <InspectionDetail
            inspection={currentInspection}
            onClose={onClose}
            onStartChat={handleStartChat}
            onCreateQuote={onCreateQuote}
            onStatusChange={async (id, status) => { await handleStatusChange(id, status); }}
            credits={credits}
            onInspectionUpdated={handleInspectionUpdated}
        />
    );
};